// Schema references in a pipeline doc's `schemas` block: the engine resolves
// an entry's `resource` by registry ID (a rename can't break a running job),
// but an ID means nothing to a person reading the code view. So the studio
// translates at its boundaries — IDs become names when a pipeline loads,
// names become IDs again when it is saved or deployed — and the code on
// screen reads `"resource": "test-schema-fork"` while the wire keeps the
// UUID. The linter accepts both forms, so nothing flags in between.

/** A registry entry as the translations need it (SchemaEntrySummary's
 *  id + optional name) plus the service whose store holds it — schemas
 *  live per service (TTTL-319), so an entry only translates refs under
 *  its own service_name. */
export interface SchemaRegistryEntry {
  id: string;
  name?: string;
  serviceName: string;
}

type Doc = Record<string, unknown>;
type SchemaEntry = Record<string, unknown> & {
  resource?: unknown;
  service_name?: unknown;
};

// The doc's schema entries, or null when the text isn't a pipeline object
// with a schemas array (nothing to translate).
function schemaEntries(doc: unknown): SchemaEntry[] | null {
  if (typeof doc !== "object" || doc === null) return null;
  const list = (doc as Doc).schemas;
  if (!Array.isArray(list)) return null;
  return list.filter(
    (e): e is SchemaEntry => typeof e === "object" && e !== null,
  );
}

// Runs a per-entry rewrite over the doc's schema entries; the text comes
// back untouched (same string) unless an entry actually changed, so a
// hand-formatted document keeps its formatting until it needs the rewrite.
function rewriteResources(
  code: string,
  rewrite: (resource: string, serviceName: string) => string,
): string {
  let doc: unknown;
  try {
    doc = JSON.parse(code);
  } catch {
    return code;
  }
  const entries = schemaEntries(doc);
  if (!entries) return code;
  let changed = false;
  for (const entry of entries) {
    if (typeof entry.resource !== "string") continue;
    const service =
      typeof entry.service_name === "string" ? entry.service_name : "";
    const next = rewrite(entry.resource, service);
    if (next !== entry.resource) {
      entry.resource = next;
      changed = true;
    }
  }
  return changed ? JSON.stringify(doc, null, 2) : code;
}

/** Load-side translation: every `resource` that is a registry ID with a
 *  name becomes that name. Unknown IDs, nameless entries and names stay. */
export function schemaRefsToNames(
  code: string,
  registry: ReadonlyArray<SchemaRegistryEntry>,
): string {
  const nameOf = new Map(
    registry
      .filter((e) => e.name)
      .map((e) => [`${e.serviceName}:${e.id}`, e.name as string]),
  );
  return rewriteResources(
    code,
    (resource, service) => nameOf.get(`${service}:${resource}`) ?? resource,
  );
}

/** Save/deploy-side translation: every `resource` that is a registry NAME
 *  becomes its ID. IDs and unknown names stay (the linter reports those).
 *  A name shared by several entries is never guessed: it stays as typed and
 *  comes back as an error — unless `preferred` settles it (the staged
 *  schema of that name, whose ID the studio knows). */
export function schemaRefsToIds(
  code: string,
  registry: ReadonlyArray<SchemaRegistryEntry>,
  options: { preferred?: ReadonlyMap<string, readonly string[]> } = {},
): { code: string; errors: string[] } {
  const ids = new Set(registry.map((e) => `${e.serviceName}:${e.id}`));
  const idsByName = new Map<string, string[]>();
  for (const entry of registry) {
    if (!entry.name) continue;
    const key = `${entry.serviceName}:${entry.name}`;
    idsByName.set(key, [...(idsByName.get(key) ?? []), entry.id]);
  }
  const errors: string[] = [];
  // Block entries are visited in document order; the n-th entry naming a
  // given service + name takes the n-th preferred id, so two staged
  // schemas sharing both (aliases "name", "name-2") keep their own ids.
  const seen = new Map<string, number>();
  const resolved = rewriteResources(code, (resource, service) => {
    if (ids.has(`${service}:${resource}`)) return resource;
    const key = `${service}:${resource}`;
    const matches = idsByName.get(key);
    if (!matches) return resource;
    if (matches.length === 1) return matches[0];
    // Keyed by service AND name: the staged schema of that name on
    // another service says nothing about this store's duplicates.
    const ordinal = seen.get(key) ?? 0;
    seen.set(key, ordinal + 1);
    const preferred = options.preferred?.get(key)?.[ordinal];
    if (preferred && matches.includes(preferred)) return preferred;
    errors.push(
      `Schema name "${resource}" matches ${matches.length} registry entries — pick it in the explorer`,
    );
    return resource;
  });
  return { code: resolved, errors };
}
